// Frontend/src/components/admin/forms/UpdatesForm.jsx
import { useEffect, useState } from 'react';
import {
  FiAlertCircle,
  FiAlertTriangle,
  FiCalendar,
  FiCheck,
  FiFilm, FiImage,
  FiInfo,
  FiMic,
  FiSave,
  FiType, FiUsers,
  FiX
} from 'react-icons/fi';
import STORAGE_PATHS from '../../../constants/storagePaths';
import { uploadFile } from '../../../services/uploadFile';
import DatePicker from '../ui/DatePicker';
import FileUpload from '../ui/FileUpload';
import Input from '../ui/Input';
import Select from '../ui/Select';

const UpdatesForm = ({ 
  initialValues, 
  onSubmit, 
  onCancel,
  ministriesOptions = []
}) => {
  const [formData, setFormData] = useState({
    title: '',
    type: 'sermon',
    speaker: '',
    date: new Date(),
    video_url: '',
    image_thumbnail: '',
    ministry: 'general',
    ...initialValues
  });

  const [imagePreview, setImagePreview] = useState(initialValues?.image_thumbnail || '');
  const [videoPreview, setVideoPreview] = useState(initialValues?.video_url || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ image: 0, video: 0 });
  const [dragActive, setDragActive] = useState({ image: false, video: false });
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    if (initialValues) {
      setFormData(prev => ({
        ...prev,
        ...initialValues,
        date: initialValues.date ? new Date(initialValues.date) : new Date()
      }));
      setImagePreview(initialValues.image_thumbnail || '');
      setVideoPreview(initialValues.video_url || '');
    }
  }, [initialValues]);

  const validateForm = () => {
    const errors = {};
    
    if (!formData.title.trim()) {
      errors.title = 'Title is required';
    }
    
    if (!formData.speaker.trim()) {
      errors.speaker = 'Speaker/Author is required';
    }
    
    if (!formData.date) {
      errors.date = 'Date is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleThumbnailChange = async (file) => {
    try {
      setUploadProgress(prev => ({ ...prev, image: 0 }));
      
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => ({ 
          ...prev, 
          image: Math.min(prev.image + 10, 90) 
        }));
      }, 100);
      
      const image_thumbnail = await uploadFile(file, STORAGE_PATHS.VIDEOS);
      
      clearInterval(progressInterval);
      setUploadProgress(prev => ({ ...prev, image: 100 }));
      
      setFormData(prev => ({ ...prev, image_thumbnail }));
      setImagePreview(URL.createObjectURL(file));
      
      setTimeout(() => {
        setUploadProgress(prev => ({ ...prev, image: 0 }));
      }, 1000);
    } catch (error) {
      console.error('Failed to upload thumbnail:', error);
      setUploadProgress(prev => ({ ...prev, image: 0 }));
    }
  };

  const handleVideoChange = async (file) => {
    try {
      setUploadProgress(prev => ({ ...prev, video: 0 }));
      
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => ({ 
          ...prev, 
          video: Math.min(prev.video + 5, 90) 
        }));
      }, 200);
      
      const video_url = await uploadFile(file, STORAGE_PATHS.VIDEOS);
      
      clearInterval(progressInterval);
      setUploadProgress(prev => ({ ...prev, video: 100 }));
      
      setFormData(prev => ({ ...prev, video_url }));
      setVideoPreview(URL.createObjectURL(file));
      
      setTimeout(() => {
        setUploadProgress(prev => ({ ...prev, video: 0 }));
      }, 1000);
    } catch (error) {
      console.error('Failed to upload video:', error);
      setUploadProgress(prev => ({ ...prev, video: 0 }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    try {
      await onSubmit({
        ...formData,
        date: formData.date.toISOString()
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getTypeIcon = (type) => {
    const icons = {
      sermon: <FiMic className="text-lg" />,
      event: <FiCalendar className="text-lg" />,
      news: <FiType className="text-lg" />,
      announcement: <FiAlertCircle className="text-lg" />
    };
    return icons[type] || <FiInfo className="text-lg" />;
  };

  const getTypeColor = (type) => {
    const colors = {
      sermon: 'bg-blue-100 text-blue-600',
      event: 'bg-green-100 text-green-600',
      news: 'bg-purple-100 text-purple-600',
      announcement: 'bg-orange-100 text-orange-600'
    };
    return colors[type] || 'bg-gray-100 text-gray-600';
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="text-center mb-10">
        <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full ${getTypeColor(formData.type)} mb-5`}>
          {getTypeIcon(formData.type)}
        </div>
        <h2 className="text-3xl font-bold text-gray-900 mb-3">
          {initialValues ? 'Update Content' : 'Create New Content'}
        </h2>
        <p className="text-gray-500 text-lg">
          Fill in the details below to {initialValues ? 'update' : 'create'} your content
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Basic Information */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-7">
          <h3 className="text-xl font-semibold text-gray-900 mb-7 flex items-center">
            <span className={`w-10 h-10 rounded-lg flex items-center justify-center mr-3 ${getTypeColor(formData.type)}`}>
              <FiInfo className="text-lg" />
            </span>
            Basic Information
          </h3>
          
          <div className="space-y-6">
            <div className="relative">
              <Input
                label="Content Title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                className={`transition-all duration-200 ${formErrors.title ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                placeholder="Enter a compelling title..."
                icon={<FiType className="text-gray-400" />}
              />
              {formErrors.title && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <FiAlertCircle className="w-4 h-4 mr-1" />
                  {formErrors.title}
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Content Type
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { value: 'sermon', label: 'Sermon', icon: <FiMic /> },
                    { value: 'event', label: 'Event', icon: <FiCalendar /> },
                    { value: 'news', label: 'News', icon: <FiType /> },
                    { value: 'announcement', label: 'Announcement', icon: <FiAlertCircle /> }
                  ].map((type) => (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => handleChange({ target: { name: 'type', value: type.value } })}
                      className={`p-3 rounded-xl border-2 transition-all duration-200 flex flex-col items-center space-y-2 ${
                        formData.type === type.value
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-200 hover:border-gray-300 text-gray-600'
                      }`}
                    >
                      <span className="text-lg">{type.icon}</span>
                      <span className="text-xs font-medium">{type.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <Select
                label="Ministry"
                name="ministry"
                value={formData.ministry}
                onChange={handleChange}
                options={ministriesOptions}
                className="transition-all duration-200"
                icon={<FiUsers className="text-gray-400" />}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="relative">
                <Input
                  label="Speaker/Author"
                  name="speaker"
                  value={formData.speaker}
                  onChange={handleChange}
                  required
                  className={`transition-all duration-200 ${formErrors.speaker ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                  placeholder="Who is presenting this content?"
                  icon={<FiMic className="text-gray-400" />}
                />
                {formErrors.speaker && (
                  <p className="mt-2 text-sm text-red-600 flex items-center">
                    <FiAlertCircle className="w-4 h-4 mr-1" />
                    {formErrors.speaker}
                  </p>
                )}
              </div>

              <div className="relative">
                <DatePicker
                  label="Date"
                  selected={formData.date}
                  onChange={(date) => {
                    setFormData(prev => ({ ...prev, date }));
                    if (formErrors.date) {
                      setFormErrors(prev => ({ ...prev, date: '' }));
                    }
                  }}
                  required
                  className={`transition-all duration-200 ${formErrors.date ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                />
                {formErrors.date && (
                  <p className="mt-2 text-sm text-red-600 flex items-center">
                    <FiAlertCircle className="w-4 h-4 mr-1" />
                    {formErrors.date}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Media Upload */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-7">
          <h3 className="text-xl font-semibold text-gray-900 mb-7 flex items-center">
            <span className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center text-purple-600 mr-3">
              <FiFilm className="text-lg" />
            </span>
            Media Files
          </h3>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Video Upload */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-gray-700">
                  Video File
                </label>
                {uploadProgress.video > 0 && (
                  <span className="text-sm text-blue-600 font-medium">
                    {uploadProgress.video}%
                  </span>
                )}
              </div>
              
              <div className="relative">
                <FileUpload
                  label=""
                  onFileChange={handleVideoChange}
                  preview={videoPreview}
                  accept="video/*"
                  className="min-h-48"
                  icon={<FiFilm className="text-gray-400 text-2xl" />}
                />
                
                {uploadProgress.video > 0 && uploadProgress.video < 100 && (
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="bg-white/90 backdrop-blur-sm rounded-lg p-3 shadow-sm">
                      <div className="flex items-center space-x-3">
                        <div className="animate-spin w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                        <div className="flex-1">
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                              style={{ width: `${uploadProgress.video}%` }}
                            ></div>
                          </div>
                        </div>
                        <span className="text-sm font-medium text-gray-600">
                          {uploadProgress.video}%
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {videoPreview && uploadProgress.video === 0 && (
                  <div className="absolute top-4 right-4">
                    <div className="bg-green-500 text-white px-3 py-1 rounded-full text-sm font-medium flex items-center">
                      <FiCheck className="w-4 h-4 mr-1" />
                      Uploaded
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Thumbnail Upload */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-gray-700">
                  Thumbnail Image
                </label>
                {uploadProgress.image > 0 && (
                  <span className="text-sm text-blue-600 font-medium">
                    {uploadProgress.image}%
                  </span>
                )}
              </div>
              
              <div className="relative">
                <FileUpload
                  label=""
                  onFileChange={handleThumbnailChange}
                  preview={imagePreview}
                  accept="image/*"
                  className="min-h-48"
                  icon={<FiImage className="text-gray-400 text-2xl" />}
                />
                
                {uploadProgress.image > 0 && uploadProgress.image < 100 && (
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="bg-white/90 backdrop-blur-sm rounded-lg p-3 shadow-sm">
                      <div className="flex items-center space-x-3">
                        <div className="animate-spin w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                        <div className="flex-1">
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                              style={{ width: `${uploadProgress.image}%` }}
                            ></div>
                          </div>
                        </div>
                        <span className="text-sm font-medium text-gray-600">
                          {uploadProgress.image}%
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {imagePreview && uploadProgress.image === 0 && (
                  <div className="absolute top-4 right-4">
                    <div className="bg-green-500 text-white px-3 py-1 rounded-full text-sm font-medium flex items-center">
                      <FiCheck className="w-4 h-4 mr-1" />
                      Uploaded
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Media Tips */}
          <div className="mt-6 bg-blue-50 rounded-xl p-4">
            <div className="flex items-start">
              <div className="text-blue-500 mr-3 mt-0.5">
                <FiInfo className="w-5 h-5" />
              </div>
              <div>
                <h4 className="text-sm font-medium text-blue-800 mb-1">
                  Media Upload Tips
                </h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• Video files should be in MP4, AVI, or MOV format</li>
                  <li>• Thumbnail images should be in JPG, PNG, or WebP format</li>
                  <li>• Recommended thumbnail size: 1280x720px for best quality</li>
                  <li>• Maximum file size: 500MB for videos, 10MB for images</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-7">
          <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-4">
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 font-medium transition-all duration-200 flex items-center justify-center"
            >
              <FiX className="w-5 h-5 mr-2" />
              Cancel
            </button>
            
            <button
              type="submit"
              disabled={isSubmitting || uploadProgress.image > 0 || uploadProgress.video > 0}
              className="px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium shadow-sm hover:shadow-md transition-all duration-200 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin w-5 h-5 mr-2 border-2 border-white border-t-transparent rounded-full"></div>
                  Saving...
                </>
              ) : (
                <>
                  <FiSave className="w-5 h-5 mr-2" />
                  {initialValues ? 'Update Content' : 'Create Content'}
                </>
              )}
            </button>
          </div>

          {/* Form Status */}
          {(uploadProgress.image > 0 || uploadProgress.video > 0) && (
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800 flex items-center">
                <FiAlertTriangle className="w-4 h-4 mr-2" />
                Please wait for files to finish uploading before submitting
              </p>
            </div>
          )}
        </div>
      </form>
    </div>
  );
};

export default UpdatesForm;